import React, { useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSDialog from "../../system-design/DSDialog";
import useLoadKlydos from "../hooks/useLoadKlydos";
import { getPrioritizedEvents } from "../../../utils/calendarEventPriority";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";
import { Klydo } from "../../../Types";
import CountrySectionHeader from "./CountrySectionHeader";
import EventItemWithPriority from "./EventItemWithPriority";
import KlydoGridDisplay from "./KlydoGridDisplay";
import KlydoModal from "../../Klydos/components/KlydoModal";

type CountryEventCardProps = {
  countryName: string;
  countryCode: string;
  events: PopulatedCalendarEvent[];
  year: number;
  month: number;
  day: number;
  allKlydosData?: PopulatedCalendarEvent[];
  onEventClick: (event: PopulatedCalendarEvent) => void;
};

const CountryEventCard = ({
  countryName,
  countryCode,
  events,
  year,
  month,
  day,
  allKlydosData,
  onEventClick,
}: CountryEventCardProps) => {
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | null>(null);
  const [klydoModalOpen, setKlydoModalOpen] = useState(false);

  const {
    populatedEvents: countryPopulatedEvents,
    klydosLoading: countryKlydosLoading,
    klydosError: countryKlydosError,
    handleLoadKlydos: handleLoadCountryKlydos,
    klydosLoaded: countryKlydosLoaded,
  } = useLoadKlydos({
    year,
    month,
    day,
    country: countryCode,
  });

  const handleKlydoClick = (klydo: Klydo) => {
    setSelectedKlydo(klydo);
    setKlydoModalOpen(true);
  };

  const isAllKlydosLoaded = !!allKlydosData;
  const displayKlydos = isAllKlydosLoaded
    ? allKlydosData.filter((e) => e.country?.code === countryCode)
    : countryPopulatedEvents || [];
  const shouldShowKlydos = isAllKlydosLoaded || countryKlydosLoaded;

  const prioritizedEvents = getPrioritizedEvents(events);
  const isMultiplePrioritized = prioritizedEvents.length > 1;

  return (
    <DSBox
      sx={{
        mb: 3,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
      }}
    >
      <CountrySectionHeader
        countryName={countryName}
        eventCount={events.length}
      />

      {events.map((event) => {
        const isPrioritized = prioritizedEvents.some((e) => e.id === event.id);
        return (
          <EventItemWithPriority
            key={event.id}
            event={event}
            isPrioritized={isPrioritized}
            isMultiplePrioritized={isMultiplePrioritized}
            onClick={() => onEventClick(event)}
          />
        );
      })}

      <DSBox
        sx={{
          mt: 2,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <DSPrimaryButton
          onClick={handleLoadCountryKlydos}
          disabled={
            countryKlydosLoading || isAllKlydosLoaded || countryKlydosLoaded
          }
          size="small"
        >
          {countryKlydosLoading
            ? "Loading..."
            : isAllKlydosLoaded
              ? "All Loaded"
              : countryKlydosLoaded
                ? `Loaded for ${countryName}`
                : `Load Klydos for ${countryName}`}
        </DSPrimaryButton>

        {shouldShowKlydos && displayKlydos.length > 0 && (
          <DSBox sx={{ mt: 2 }}>
            {displayKlydos.map((event) => {
              if (!event.klydos || event.klydos.length === 0) return null;

              return (
                <DSBox
                  key={event.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "white.5",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <DSTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    {event.name} ({event.klydos.length} klydo
                    {event.klydos.length > 1 ? "s" : ""})
                  </DSTypography>
                  <KlydoGridDisplay
                    klydos={event.klydos}
                    onKlydoClick={handleKlydoClick}
                  />
                </DSBox>
              );
            })}
          </DSBox>
        )}

        {countryKlydosError && (
          <DSTypography color="error" variant="body2" sx={{ mt: 1 }}>
            Error loading klydos: {countryKlydosError.message}
          </DSTypography>
        )}
      </DSBox>

      {selectedKlydo && (
        <DSDialog
          open={klydoModalOpen}
          onClose={() => setKlydoModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={setKlydoModalOpen}
          />
        </DSDialog>
      )}
    </DSBox>
  );
};

export default CountryEventCard;
